import React, { useId } from "react";
import PropTypes from "prop-types";
import PageIndicator from "./PageIndicator";

/**
 * The pagination bar both public components render.
 *
 * They used to carry a copy each, and the copies drifted: one reset to the first
 * page when the page size changed and the other didn't, one disabled its arrows on
 * `=== totalPages` and the other on `>=`, and only one had dark mode. One
 * implementation is the fix for that class of bug, with each of them left as the
 * adapter its own callers already expect.
 */

/**
 * WCAG 2.5.8 asks for 24×24 CSS px; the arrows are glyphs, so the box is padded.
 * gray-500 (#6B7280) is 4.83:1 on white — the lightest step on the scale that
 * still clears 1.4.3's 4.5:1, with gray-400 at 2.85:1 failing even the 3:1 that
 * non-text UI would need.
 */
const ARROW =
  "inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md " +
  "text-gray-500 hover:bg-gray-100 hover:text-[#223688] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#223688] " +
  "disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-600 " +
  "dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover dark:hover:text-[#6DE1C2] " +
  "transition-none";

/**
 * The page field: the same underline the selector wears, with no ring around it.
 * Focus darkens that underline instead — still a visible focus indicator (2.4.7),
 * without boxing the field in.
 */
const PAGE_INPUT =
  "border-b-2 border-gray-400 bg-transparent text-gray-800 " +
  "focus-visible:border-gray-700 focus-visible:outline-none " +
  "dark:border-acs-dark-border dark:text-acs-dark-text dark:focus-visible:border-acs-dark-subtext " +
  "transition-none";

const SELECT =
  "border-b-2 border-gray-400 bg-transparent text-gray-800 " +
  "focus-visible:border-[#223688] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#223688] " +
  "dark:border-acs-dark-border dark:text-acs-dark-text dark:focus-visible:border-[#6DE1C2] " +
  "transition-none";

/** "1–50 of 384", the rows this page covers — omitted when nobody passed a total. */
const itemRange = (page, perPage, totalItems) => {
  if (!Number.isFinite(totalItems) || totalItems <= 0) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalItems);
  return `${from.toLocaleString()}–${to.toLocaleString()} of ${totalItems.toLocaleString()}`;
};

const PaginationBar = ({
  perPageLabel,
  perPage,
  perPageOptions,
  onPerPageChange,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const pageCount = Math.max(1, totalPages);
  // Clamped once, here: a page left behind by a filter that shrank the results is
  // otherwise a next arrow that looks live and does nothing.
  const page = Math.min(Math.max(currentPage || 1, 1), pageCount);
  const perPageId = useId();
  const range = itemRange(page, perPage, totalItems);

  const goTo = (next) => {
    if (next !== page) onPageChange?.(next);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-white p-fluid-1 text-gray-800 transition-none dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text">
      {/* Off the bar's left edge, rather than flush against it. */}
      <div className="flex items-center pl-fluid-2 font-roboto text-fluid-base">
        {/* Muted to match the row-range readout on the other side of the bar —
            both are labels for the numbers beside them, not the numbers. */}
        <label
          htmlFor={perPageId}
          className="mr-fluid-2 text-gray-600 dark:text-acs-dark-subtext"
        >
          {perPageLabel}
        </label>
        <select
          id={perPageId}
          value={perPage}
          onChange={(e) => {
            onPerPageChange?.(Number(e.target.value));
            // Page 1 every time: a bigger page size can leave the page the user is
            // on past the end of the results.
            onPageChange?.(1);
          }}
          className={SELECT}
        >
          {perPageOptions.map((num) => (
            <option
              key={num}
              value={num}
              className="bg-white text-gray-800 dark:bg-acs-dark-surface dark:text-acs-dark-text"
            >
              {num}
            </option>
          ))}
        </select>
      </div>

      <nav
        aria-label="Pagination"
        className="flex items-center font-roboto text-fluid-base"
      >
        {range && (
          <span className="mr-4 text-gray-600 dark:text-acs-dark-subtext">{range}</span>
        )}

        <button
          type="button"
          onClick={() => goTo(1)}
          disabled={page <= 1}
          title="First page"
          aria-label="First page"
          className={`ml-2 mr-1 ${ARROW}`}
        >
          ❮❮
        </button>
        <button
          type="button"
          onClick={() => goTo(page - 1)}
          disabled={page <= 1}
          title="Previous page"
          aria-label="Previous page"
          className={`ml-1 mr-2 ${ARROW}`}
        >
          ❮
        </button>

        <PageIndicator
          currentPage={page}
          totalPages={pageCount}
          onPageChange={onPageChange}
          className={PAGE_INPUT}
        />

        <button
          type="button"
          onClick={() => goTo(page + 1)}
          disabled={page >= pageCount}
          title="Next page"
          aria-label="Next page"
          className={`ml-2 mr-1 ${ARROW}`}
        >
          ❯
        </button>
        <button
          type="button"
          onClick={() => goTo(pageCount)}
          disabled={page >= pageCount}
          title="Last page"
          aria-label="Last page"
          className={`ml-1 mr-2 ${ARROW}`}
        >
          ❯❯
        </button>

        {/* Clicking an arrow leaves focus on the arrow, so where the user landed is
            only announced if something says so. */}
        <span role="status" aria-live="polite" className="sr-only">
          {`Page ${page} of ${pageCount}${range ? `, showing ${range}` : ""}`}
        </span>
      </nav>
    </div>
  );
};

PaginationBar.propTypes = {
  perPageLabel: PropTypes.string.isRequired,
  perPage: PropTypes.number.isRequired,
  perPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  onPerPageChange: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  /** Rows across every page. Without it the range readout is left out. */
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
};

export default PaginationBar;
